import { IMPACT_CONFIG, ImpactYear } from "hooks/maps/useMapImpactSlider";
export type ESGScopeEmissions = {
  scope1: number; // in metric tons of CO2 equivalent (tCO2e)
  scope2: number;
  scope3: number;
};

export type ESGMetrics = {
  savedAreaMeters: number;
  savedAreaKilometers: number;
  savedAreaSoccerFields: number;
  savedTrees: number;
  savedCO2: number;
  kronerDonated: number;
};

const YEAR: ImpactYear = "2026";
const config = IMPACT_CONFIG[YEAR];

/**
 * Converts CO2 in metric tons to the respective impact metrics based on the 2026 formulas.
 */
export const convertCo2ToMetrics = (co2Tons: number): ESGMetrics => {
  if (co2Tons <= 0 || isNaN(co2Tons)) {
    return {
      savedAreaMeters: 0,
      savedAreaKilometers: 0,
      savedAreaSoccerFields: 0,
      savedTrees: 0,
      savedCO2: 0,
      kronerDonated: 0,
    };
  }

  // savedCO2 (tons) = (savedAreaMeters / 10000) * co2Multiplier
  // savedAreaMeters = (savedCO2 * 10000) / co2Multiplier
  const savedAreaMeters = (co2Tons * 10000) / config.co2Multiplier;

  // savedTrees = (savedAreaMeters / 10000) * treeMultiplier
  const savedTrees = (savedAreaMeters / 10000) * config.treeMultiplier;

  // kronerDonated = savedAreaMeters / currencyMultiplier
  const kronerDonated = savedAreaMeters / config.currencyMultiplier;

  const savedAreaKilometers = savedAreaMeters / 1000000;

  const savedAreaSoccerFields = savedAreaMeters / config.footballFieldArea;

  return {
    savedAreaMeters: parseFloat(savedAreaMeters.toFixed(2)),
    savedAreaKilometers: parseFloat(savedAreaKilometers.toFixed(4)),
    savedAreaSoccerFields: parseFloat(savedAreaSoccerFields.toFixed(2)),
    savedTrees: parseFloat(savedTrees.toFixed(2)),
    savedCO2: parseFloat(co2Tons.toFixed(2)),
    kronerDonated: parseFloat(kronerDonated.toFixed(2)),
  };
};

/**
 * Converts combined Scope 1, Scope 2, and Scope 3 emissions into aggregated metrics.
 */
export const convertESGToMetrics = (emissions: ESGScopeEmissions): ESGMetrics => {
  const totalCo2 = emissions.scope1 + emissions.scope2 + emissions.scope3;
  return convertCo2ToMetrics(totalCo2);
};

export type ESGSourceContexts = {
  scope1?: string;
  scope2?: string;
  scope3?: string;
};

export type ESGExtractPayload = { text: string } | { base64Data: string; mimeType: string };

const isPdfFile = (file: File): boolean =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

const isPlainTextFile = (file: File): boolean =>
  file.type === "text/plain" || file.name.toLowerCase().endsWith(".txt");

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

export const prepareEsgExtractPayload = async (file: File): Promise<ESGExtractPayload> => {
  if (isPdfFile(file)) {
    const { pdfFileToText } = await import("./pdfToText");
    const text = await pdfFileToText(file);
    if (!text.trim()) {
      throw new Error(
        "No text found in PDF. The document may be scanned or image-only."
      );
    }
    return { text };
  }

  if (isPlainTextFile(file)) {
    const text = await file.text();
    if (!text.trim()) {
      throw new Error("The text file is empty.");
    }
    return { text };
  }

  return {
    base64Data: await fileToBase64(file),
    mimeType: file.type || "application/octet-stream",
  };
};

export const extractEmissionsWithGemini = async (
  payload: ESGExtractPayload
): Promise<{ emissions: ESGScopeEmissions; contexts: ESGSourceContexts }> => {
  try {
    const url = `${import.meta.env.REACT_APP_REST_URL}/v1/esg/extract`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || "Failed to extract emissions from backend");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Backend ESG Extraction Error:", error);
    throw error;
  }
};
